import { ClassName } from '@boxcryptor-web/shared/class-name';
import { Disclosure, Transition } from '@headlessui/react';
import classnames from 'classnames';
import slugger from 'github-slugger';
import React, { ComponentPropsWithoutRef, ElementType, useRef } from 'react';
import { useLevel } from 'react-accessible-headings';
import ReactMarkdown from 'react-markdown';
import { CodeProps, HeadingProps, ReactMarkdownProps } from 'react-markdown/lib/ast-to-react';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { FirstHeadingLevelContextProvider, useFirstHeadingLevelContext } from './headings/LevelContext';
import { DynamicIcon } from './icons';
import { InfoBox } from './InfoBox';
import { ExternalLink, InternalLink, useInternalLinkContext } from './link';
import { Slide, SlideShow } from './slide-show/SlideShow';

type ComponentProps<Element extends ElementType> = ComponentPropsWithoutRef<Element> & ReactMarkdownProps;

export const createAnchorTag = (value: string) => slugger.slug(value);
export const Heading: React.FC<HeadingProps> = (props) => {
  const context = useFirstHeadingLevelContext();
  const { node, className, children, level, ...other } = props;
  if (!context.firstHeadingLevel) {
    context.firstHeadingLevel = level;
  }
  const globalLevel = useLevel();
  const currentLevel = globalLevel + level - context.firstHeadingLevel;
  const styleLevel = level - (context.firstHeadingLevel - 1);
  const id = typeof children[0] === 'string' ? createAnchorTag(children[0]) : undefined;
  if (currentLevel <= 6) {
    const heading = `h${currentLevel}`;
    return React.createElement(heading, { className: className || `h${styleLevel}`, ...other, id }, children);
  }
  return (
    <span className={className || 'h6'} id={id}>
      {children}
    </span>
  );
};

const Misc: React.FC<CodeProps> = (props) => {
  const { children, className } = props;
  const text = Array.isArray(children) ? (children[0] as string) : (children as string);

  if (className === 'language-video' && text.includes('.mp4')) {
    const [url, previewImage] = text.split('\n');
    return <Video url={url} previewImage={previewImage} />;
  }

  if (className === 'language-slideshow') {
    const markdownImageRegex = /!\[(?<alt>[^\]]*)\]\((?<src>.*?)(?="|\))(?:"(?<description>.*)")?\)/;
    const markdownImages = text.split('\n').filter((it) => it !== '');
    const slides: Slide[] = markdownImages
      .map((it) => {
        const match = it.match(markdownImageRegex);
        if (!match || !match.groups) {
          return undefined;
        }
        const imgUrl = match.groups['src'];
        if (!imgUrl) {
          return undefined;
        }
        const altText = match.groups['alt'];
        const description = match.groups['description'];
        return { imgUrl, altText, description };
      })
      .filter((it) => it) as Slide[];
    return (
      <SlideShow className="mx-auto w-full max-w-4xl">
        {slides.map((it, index) => (
          <div key={index} className="w-full flex flex-col items-center justify-center">
            <img src={it.imgUrl} alt={it.altText} className="my-1" />
            {it.description && <div className={classnames('w-full ', 'text-gray-700 font-normal text-center mb-4')}>{it.description}</div>}
          </div>
        ))}
      </SlideShow>
    );
  }

  if (className === 'language-accordion') {
    const [title, ...rest] = text.split('\n');
    const content = rest.join('\n');
    return <Accordion title={title} content={content} />;
  }

  if (className === 'language-text') {
    return <Markdown>{text}</Markdown>;
  }

  if (className === 'language-hint') {
    return <InfoBox variant="info" infoText={<Markdown>{text}</Markdown>} />;
  }
  if (className === 'language-warning') {
    return <InfoBox variant="warning" infoText={<Markdown>{text}</Markdown>} />;
  }
  if (className === 'language-danger') {
    return <InfoBox variant="danger" infoText={<Markdown>{text}</Markdown>} />;
  }

  return <Code {...props} />;
};

const Accordion: React.FC<{ title: string; content: string }> = ({ title, content }) => {
  const contentSpace = useRef<HTMLDivElement>(null);
  return (
    <Disclosure>
      {({ open }) => {
        const contentHeight = open ? `${contentSpace.current?.scrollHeight || 0}px` : '0px';
        return (
          <div className="my-2 border-2 border-gray-200 rounded-lg">
            <Disclosure.Button className="flex flex-row items-center gap-2 p-2 w-full">
              <DynamicIcon identifier="chevron-right" className={`h-4 ${open ? 'transform rotate-90' : ''}`} />
              <div className="prose max-w-full">
                <Markdown>{title}</Markdown>
              </div>
            </Disclosure.Button>
            <Transition
              show={open}
              style={{ maxHeight: contentHeight }}
              className="overflow-clip transition-max-height duration-200 ease-in-out"
            >
              <Disclosure.Panel static ref={contentSpace}>
                <div className="prose prose-sm max-w-full p-2 pt-0">
                  <Markdown>{content}</Markdown>
                </div>
              </Disclosure.Panel>
            </Transition>
          </div>
        );
      }}
    </Disclosure>
  );
};

const Video: React.FC<{ url: string; previewImage: string }> = ({ url, previewImage }) => (
  <div className="mx-auto w-full flex items-center justify-center">
    <video src={url} className="w-full max-w-4xl h-full" preload="metadata" controls poster={previewImage}></video>
  </div>
);

const A: React.FC<ComponentProps<'a'>> = (props) => {
  const { href = '', className, children } = props;
  const context = useInternalLinkContext();
  return context.shouldRenderAsInternalLink(href) ? (
    <InternalLink to={href} className={className}>
      {children}
    </InternalLink>
  ) : (
    <ExternalLink href={href} className={className} target="_blank">
      {children}
    </ExternalLink>
  );
};

const Code: React.FC<CodeProps> = (props) =>
  props.inline ? (
    <code className={props.className}>{props.children}</code>
  ) : (
    <pre>
      <code className={props.className}>{props.children}</code>
    </pre>
  );

const Pre: React.FC<ComponentProps<'pre'>> = (props) => {
  const { className, children } = props;
  return <div className={className}>{children}</div>;
};

export const Markdown: React.FC<
  ClassName<{
    children: string;
    alternativeHeadingComponents?: Array<{ test: RegExp; Component: React.FC<HeadingProps> }>;
  }>
> = (props) => {
  const { children, alternativeHeadingComponents } = props;

  const headingComponentSelector = (props: HeadingProps) => {
    if (alternativeHeadingComponents) {
      for (const alternativeHeadingComponent of alternativeHeadingComponents) {
        if (props.node.children.some((it) => it.type === 'text' && alternativeHeadingComponent.test.test(it.value))) {
          return <alternativeHeadingComponent.Component {...props} />;
        }
      }
    }
    return <Heading {...props} />;
  };

  return (
    <FirstHeadingLevelContextProvider>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          h1: headingComponentSelector,
          h2: headingComponentSelector,
          h3: headingComponentSelector,
          h4: headingComponentSelector,
          h5: headingComponentSelector,
          h6: headingComponentSelector,
          pre: Pre,
          code: Misc,
          a: A,
        }}
        className={props.className}
      >
        {children}
      </ReactMarkdown>
    </FirstHeadingLevelContextProvider>
  );
};
